import { Navigate, useParams } from 'react-router';
import { useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import useAnalytics from '../../hooks/useAnalytics';
import { PRODUCT_AND_SERVICES } from '../../constants/analytics';
import productsAndServices from '../../assets/json/productsAndServices';
import { fetchPricesByCountry } from '../../apis/reference';
import Loader from '../../features/Loader';
import ContentLayout from '../../components/ContentLayout';
import { useCustomSnackbar } from '../../features/CustomSnackbar';
import { STRINGS } from '../../constants';
import { Error } from '../../features/ErrorBoundary';
import PricesContext from '../../components/ContentPages/PricesContext';
import { calculateDomesticPrices } from './helpers';
import { DEFAULT_BUS_ZONE, DEFAULT_COUNTRY, DEFAULT_PAGE } from './constants';

const ProductAndServices = () => {
  const { Trackable } = useAnalytics();
  const { service, country } = useParams();
  const { showError } = useCustomSnackbar();
  const [prices, setPrices] = useState({});
  const key = `${service}${country ? `/${country}` : ''}`;
  const pageData = productsAndServices[key] || {};
  const { bannerSrc, title, components, countryCode, busZone } = pageData;
  const isDomesticPage = useMemo(() => !country || country === 'uk', [country]);
  const domesticPrices = useMemo(
    () =>
      isDomesticPage && !isEmpty(prices) ? calculateDomesticPrices(prices) : {},
    [isDomesticPage, prices]
  );

  const getPricesData = useCallback(async () => {
    const countryParam = isDomesticPage ? DEFAULT_COUNTRY : countryCode;
    const busZoneParam = isDomesticPage ? DEFAULT_BUS_ZONE : busZone;
    try {
      const res = await fetchPricesByCountry(countryParam, busZoneParam);
      setPrices(res.data);
    } catch (error) {
      showError({ message: STRINGS.PRICES_ERROR });
    }
  }, [country, countryCode, busZone, isDomesticPage]);

  if (!service) {
    return <Navigate to={DEFAULT_PAGE} replace={true} />;
  }

  if (isEmpty(pageData)) {
    return <Error />;
  }

  return (
    <Trackable loadId={PRODUCT_AND_SERVICES.LOAD}>
      <Loader promiseFn={getPricesData} triggerOnMount={true}>
        <PricesContext.Provider value={{ prices, domesticPrices }}>
          <ContentLayout
            bannerSrc={bannerSrc}
            title={title}
            components={components}
          />
        </PricesContext.Provider>
      </Loader>
    </Trackable>
  );
};

export default ProductAndServices;
